import React, { useEffect, useRef, useState } from 'react';
import { RiArrowLeftSLine, RiArrowRightSLine } from 'react-icons/ri';
import styles from './StoryPlate.module.css';

/* Story Time select: holographic cards you cycle through. The card facing you
   is the pick; its neighbours wait turned away on either side. Swipe, tap a
   neighbour, the arrows or the arrow keys move on.

   Kept cheap on purpose: positions are plain CSS transforms with a transition
   (no animation loop), and there are no blur or mask filters, which is what
   made the earlier plate slow on a phone.

   items: [{ id, title, caption, art, stack, accent, focus }]
   stack: more posters of the same genre; the card facing you fades through them.
   extra: (item) => node, shown under the cards for the pick (the audio sample). */

const reducedMotion = () => typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function Layer({ src, position, eager, shown }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return null;
  return <img className={`${styles.art} ${shown ? styles.artShown : ''}`} src={src} alt="" draggable={false} decoding="async" loading={eager ? 'eager' : 'lazy'} style={position ? { objectPosition: position } : undefined} onError={() => setFailed(true)} />;
}

/* A card's posters. Only the card facing you fades through them, and the
   extra posters are not requested until that card has faced you once. */
function Posters({ art, stack, position, eager, playing }) {
  const [index, setIndex] = useState(0);
  const [woken, setWoken] = useState(false);
  const extras = stack || [];
  const count = 1 + extras.length;

  useEffect(() => {
    if (!playing || count < 2) { setIndex(0); return undefined; }
    setWoken(true);
    if (reducedMotion()) return undefined;
    const timer = setInterval(() => setIndex((i) => (i + 1) % count), 2400);
    return () => clearInterval(timer);
  }, [playing, count]);

  return (
    <>
      <Layer src={art} position={position} eager={eager} shown={index === 0} />
      {woken && extras.map((src, i) => <Layer key={src} src={src} eager shown={index === i + 1} />)}
    </>
  );
}

export default function StoryPlate({ items, value, onChange, disabled = false, label, tone = '', extra }) {
  const count = items ? items.length : 0;
  const picked = count ? items.findIndex((item) => item.id === value) : -1;
  const [front, setFront] = useState(picked >= 0 ? picked : 0);
  const [touched, setTouched] = useState(false);
  const swipe = useRef({ pointer: null, x: 0, y: 0, moved: false });

  useEffect(() => { if (picked >= 0) setFront(picked); }, [picked]);

  // Until something is picked, the cards cycle by themselves.
  useEffect(() => {
    if (picked >= 0 || touched || disabled || count < 2 || reducedMotion()) return undefined;
    const timer = setInterval(() => setFront((i) => (i + 1) % count), 2600);
    return () => clearInterval(timer);
  }, [picked, touched, disabled, count]);

  if (!count) return null;

  const pick = (index) => {
    if (disabled) return;
    const next = ((index % count) + count) % count;
    setTouched(true);
    setFront(next);
    if (items[next].id !== value) onChange(items[next].id);
  };

  const onPointerDown = (event) => {
    swipe.current = { pointer: event.pointerId, x: event.clientX, y: event.clientY, moved: false };
  };

  const onPointerUp = (event) => {
    const s = swipe.current;
    if (s.pointer !== event.pointerId) return;
    s.pointer = null;
    const dx = event.clientX - s.x;
    const dy = event.clientY - s.y;
    if (Math.abs(dx) < 36 || Math.abs(dx) < Math.abs(dy)) return;
    s.moved = true;
    pick(front + (dx < 0 ? 1 : -1));
  };

  const onKeyDown = (event) => {
    const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    const dir = keys[event.key];
    if (!dir) return;
    event.preventDefault();
    pick(front + dir);
  };

  const current = picked >= 0 ? items[picked] : null;
  const accent = (items[front] && items[front].accent) || 'var(--primary-color)';

  return (
    <div className={`${styles.scene} ${tone === 'cold' ? styles.cold : ''}`} style={{ '--accent': accent }}>
      <div
        className={styles.stage}
        role="radiogroup"
        aria-label={label}
        onKeyDown={onKeyDown}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerCancel={() => { swipe.current.pointer = null; }}
      >
        {items.map((item, index) => {
          // how many places from the front, by the shorter way round
          let offset = (((index - front) % count) + count) % count;
          if (offset > count / 2) offset -= count;
          const active = value === item.id;
          const isFront = offset === 0;
          const far = Math.abs(offset) > 2;
          return (
            <div
              key={item.id}
              role="radio"
              aria-checked={active}
              aria-disabled={disabled || undefined}
              tabIndex={disabled ? -1 : (isFront ? 0 : -1)}
              className={`${styles.card} ${isFront ? styles.cardFront : ''} ${active ? styles.cardPicked : ''}`}
              style={{
                '--accent': item.accent || 'var(--primary-color)',
                transform: `translateX(-50%) translateX(${offset * 66}%) scale(${isFront ? 1 : Math.max(0.6, 0.84 - (Math.abs(offset) - 1) * 0.12)}) rotateY(${offset * -32}deg)`,
                opacity: far ? 0 : (isFront ? 1 : 0.55 - (Math.abs(offset) - 1) * 0.25),
                zIndex: 10 - Math.abs(offset),
                pointerEvents: far ? 'none' : undefined,
              }}
              onClick={() => { if (swipe.current.moved) { swipe.current.moved = false; return; } pick(index); }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); pick(index); }
              }}
            >
              <span className={styles.face}>
                <Posters art={item.art} stack={item.stack} position={item.focus} eager={Math.abs(offset) <= 1} playing={isFront} />
                <span className={styles.scan} />
                <span className={styles.sheen} />
              </span>
              <span className={styles.name}>{item.title}</span>
            </div>
          );
        })}

        {count > 1 && (
          <>
            <button type="button" className={`${styles.turn} ${styles.turnLeft}`} onClick={() => pick(front - 1)} onPointerDown={(e) => e.stopPropagation()} disabled={disabled} aria-label="Previous" tabIndex={-1}><RiArrowLeftSLine size={22} /></button>
            <button type="button" className={`${styles.turn} ${styles.turnRight}`} onClick={() => pick(front + 1)} onPointerDown={(e) => e.stopPropagation()} disabled={disabled} aria-label="Next" tabIndex={-1}><RiArrowRightSLine size={22} /></button>
          </>
        )}
      </div>

      <div className={styles.readout} aria-live="polite">
        {current ? (
          <div key={current.id} className={styles.readoutIn}>
            {current.caption && <span className={styles.readoutCaption}>{current.caption}</span>}
            {extra && <span className={styles.readoutExtra}>{extra(current)}</span>}
          </div>
        ) : (
          <span className={styles.hint}>Swipe through. Tap one to pick it.</span>
        )}
      </div>
    </div>
  );
}
